// icons.tsx — calm line-icon set. Stroke = currentColor. Ported from the
// design's icon set (guide/.../app/icons.jsx).
import React from 'react';

export type IconName =
  | 'today' | 'journey' | 'reflections' | 'you'
  | 'play' | 'pause' | 'back15' | 'fwd15'
  | 'chevDown' | 'chevRight' | 'chevLeft' | 'more'
  | 'check' | 'headphones' | 'clock' | 'pencil' | 'bookmark'
  | 'book' | 'film' | 'leaf' | 'settings' | 'compass' | 'shield'
  | 'gauge' | 'chart' | 'refresh' | 'voice' | 'moon' | 'plus' | 'heart' | 'sparkle'
  | 'download' | 'tool' | 'external' | 'audio' | 'expand' | 'search' | 'lock' | 'trash'
  | 'emoPanic' | 'emoOverwhelm' | 'emoEdgy' | 'emoRumination' | 'emoNumb' | 'emoFlat';

interface IconProps {
  name: IconName;
  size?: number;
  sw?: number;
  style?: React.CSSProperties;
  'aria-hidden'?: boolean;
}

const S = (
  children: React.ReactNode,
  size: number,
  sw: number,
  style?: React.CSSProperties,
) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block', ...style }}
  >
    {children}
  </svg>
);

const paths: Record<IconName, React.ReactNode> = {
  today: (
    <>
      <path d="M4 17.5h16" />
      <path d="M7.5 17.5a4.5 4.5 0 0 1 9 0" />
      <path d="M12 5.5v2M5.5 8.2l1.4 1.4M18.5 8.2l-1.4 1.4M2.5 13h1.6M19.9 13h1.6" />
    </>
  ),
  journey: (
    <>
      <path d="M7 20c0-3 4-3 4-6.5S7 10 7 6.5" />
      <path d="M11.5 7.5c1.5 1 5 1 5 4.5s-3.5 3.5-3.5 6" />
      <circle cx="7" cy="5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="17" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="9" cy="20" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  reflections: (
    <>
      <path d="M19 5c-7 0-12 4-12 11 0 0 0 1 0 2" />
      <path d="M19 5c0 7-4 11-11 11" />
      <path d="M16 8c-4 0-7 2.5-8 6" />
    </>
  ),
  you: (
    <>
      <circle cx="12" cy="8.5" r="3.4" />
      <path d="M5.5 19.5c1-3.4 3.6-5 6.5-5s5.5 1.6 6.5 5" />
    </>
  ),
  play: <path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none" />,
  pause: (
    <>
      <rect x="7.5" y="5.5" width="3" height="13" rx="1" fill="currentColor" stroke="none" />
      <rect x="13.5" y="5.5" width="3" height="13" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
  back15: (
    <>
      <path d="M5 8a8 8 0 1 1-1.5 6.5" />
      <path d="M5 4.5V8.5H9" />
    </>
  ),
  fwd15: (
    <>
      <path d="M19 8a8 8 0 1 0 1.5 6.5" />
      <path d="M19 4.5V8.5H15" />
    </>
  ),
  chevDown: <path d="M6 9.5l6 6 6-6" />,
  chevRight: <path d="M9 6l6 6-6 6" />,
  chevLeft: <path d="M15 6l-6 6 6 6" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 6.5" />,
  headphones: (
    <>
      <path d="M5 13v-1a7 7 0 0 1 14 0v1" />
      <rect x="3.5" y="12.5" width="3.5" height="6" rx="1.5" />
      <rect x="17" y="12.5" width="3.5" height="6" rx="1.5" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
      <circle cx="12" cy="15" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M10 4.5h4a1 1 0 0 1 1 1V7H9V5.5a1 1 0 0 1 1-1z" />
      <path d="M6.5 7l.9 12a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4l.9-12" />
      <path d="M10.5 11v6M13.5 11v6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.3l2.6 1.6" />
    </>
  ),
  pencil: (
    <>
      <path d="M14.5 5.5l4 4L9 19l-4.5 1L5.5 15.5z" />
      <path d="M13 7l4 4" />
    </>
  ),
  bookmark: <path d="M7 4.5h10v15l-5-3.5-5 3.5z" />,
  book: (
    <>
      <path d="M12 6.5C10.5 5.2 8.5 4.7 5.5 5v13c3-0.3 5 0.2 6.5 1.5" />
      <path d="M12 6.5c1.5-1.3 3.5-1.8 6.5-1.5v13c-3-0.3-5 0.2-6.5 1.5z" />
      <path d="M12 6.5v13" />
    </>
  ),
  film: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="2.5" />
      <path d="M10 9.5v5l4-2.5z" fill="currentColor" stroke="none" />
    </>
  ),
  leaf: (
    <>
      <path d="M5 19c-1-7 4-13 14-14 1 9-4 14-11 14-1.5 0-3-1.4-3-3z" />
      <path d="M9 16c1.5-4 4-6.5 7.5-8" />
    </>
  ),
  settings: (
    <>
      <path d="M19.14 12.94a7.49 7.49 0 0 0 0-1.88l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.61-.22l-2.39.96a7.3 7.3 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54a7.3 7.3 0 0 0-1.62.94l-2.39-.96a.5.5 0 0 0-.61.22L2.69 8.84a.5.5 0 0 0 .12.64l2.03 1.58a7.49 7.49 0 0 0 0 1.88l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .61.22l2.39-.96c.5.38 1.04.7 1.62.94l.36 2.54a.5.5 0 0 0 .5.42h3.84a.5.5 0 0 0 .5-.42l.36-2.54a7.3 7.3 0 0 0 1.62-.94l2.39.96a.5.5 0 0 0 .61-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58z" />
      <circle cx="12" cy="12" r="3.2" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M16.2 7.8l-2.1 6.3-6.3 2.1 2.1-6.3z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.5l7 2.5v5c0 5-3.2 8-7 9.5C8.2 19 5 16 5 11V6z" />
      <path d="M9 11.5l2 2 4-4" />
    </>
  ),
  gauge: (
    <>
      <path d="M5 17a8 8 0 1 1 14 0" />
      <path d="M12 17l3.5-5" />
    </>
  ),
  chart: (
    <>
      <path d="M4 4v15a1 1 0 0 0 1 1h15" />
      <path d="M7.5 14l3.5-3.5 3 3L20 7" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 4v5h-5" />
    </>
  ),
  voice: (
    <>
      <rect x="9.5" y="3.5" width="5" height="11" rx="2.5" />
      <path d="M6 11.5a6 6 0 0 0 12 0" />
      <path d="M12 17.5V21M9 21h6" />
    </>
  ),
  moon: <path d="M19 13.5A7.5 7.5 0 0 1 10.5 5a7.5 7.5 0 1 0 8.5 8.5z" />,
  plus: <path d="M12 6v12M6 12h12" />,
  heart: <path d="M12 19.5C6.5 16 4 12.7 4 9.5A3.8 3.8 0 0 1 12 7a3.8 3.8 0 0 1 8 2.5c0 3.2-2.5 6.5-8 10z" />,
  sparkle: (
    <path
      d="M12 4c.6 3.8 1.8 5 5.5 5.5C13.8 10 12.6 11.2 12 15c-.6-3.8-1.8-5-5.5-5.5C10.2 9 11.4 7.8 12 4z"
      fill="currentColor"
      stroke="none"
    />
  ),
  download: (
    <>
      <path d="M12 4v10M8 11l4 4 4-4" />
      <path d="M5 18.5h14" />
    </>
  ),
  tool: (
    <>
      <path d="M14.5 6a3.5 3.5 0 0 0-4.7 4.2L4 16v3h3l5.8-5.8A3.5 3.5 0 0 0 18 8.5l-2.3 2.3-1.7-1.7L16.3 6.8A3.5 3.5 0 0 0 14.5 6z" />
    </>
  ),
  external: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4l-8.5 8.5" />
      <path d="M18 13.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5.5" />
    </>
  ),
  audio: (
    <>
      <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
    </>
  ),
  expand: (
    <>
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
    </>
  ),
  // ── emotional-state icons (used by the Calm Your Body emotion pills) ──
  // Panic / acute anxiety — a lightning bolt.
  emoPanic: <path d="M13 2 4 13h7l-1 9 10-12h-7l1-8z" />,
  // Overwhelm / flooded — stacked waves.
  emoOverwhelm: (
    <>
      <path d="M2.5 8c2.4-3 4.8-3 7.2 0s4.8 3 7.2 0" />
      <path d="M2.5 12c2.4-3 4.8-3 7.2 0s4.8 3 7.2 0" />
      <path d="M2.5 16c2.4-3 4.8-3 7.2 0s4.8 3 7.2 0" />
    </>
  ),
  // Edgy / wired — a jittery pulse.
  emoEdgy: <path d="M2 12h3l2-6 3 11 3-13 3 11 3-3h2" />,
  // Rumination / looping thoughts — a closed loop with an arrowhead.
  emoRumination: (
    <>
      <path d="M20 12a8 8 0 1 1-2.4-5.7" />
      <path d="M20 3.5V7h-3.5" />
    </>
  ),
  // Dissociated / numb — a faded (dashed) ring around a present centre.
  emoNumb: (
    <>
      <circle cx="12" cy="12" r="8.2" strokeDasharray="2 3.4" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    </>
  ),
  // Flat / low energy — an all-but-empty battery.
  emoFlat: (
    <>
      <rect x="2.5" y="8.5" width="15" height="7" rx="2" />
      <path d="M20.5 11v2" />
    </>
  ),
};

export function Icon({ name, size = 24, sw = 1.6, style }: IconProps) {
  const body = paths[name];
  if (!body) return null;
  return S(body, size, sw, style);
}

/** Brand mark — two overlapping circles (gold outline + striped gold disc). */
export function BrandMark({
  size = 26,
  gold = '#E8B84B',
  stripe = '#2C2A25',
}: {
  size?: number;
  gold?: string;
  stripe?: string;
}) {
  return (
    <svg
      width={size}
      height={size * 0.72}
      viewBox="0 0 50 36"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <circle cx="18" cy="18" r="14.5" stroke={gold} strokeWidth="2.4" />
      <circle cx="32" cy="18" r="14.5" fill={gold} />
      <g stroke={stripe} strokeWidth="1.5" strokeLinecap="round" opacity="0.85">
        <path d="M26 12h12M24 18h16M26 24h12" />
      </g>
    </svg>
  );
}

/** Renurture wordmark. Uses the supplied brand asset, falls back to the mark + text. */
export function Wordmark({ h = 19, dark = false }: { h?: number; dark?: boolean }) {
  const src = dark ? './brand/renurture-wordmark-dark.png' : './brand/renurture-wordmark.png';
  return <img src={src} alt="ReNurture" style={{ height: h, display: 'block' }} />;
}
